'use client';

import { useState } from 'react';
import { motion } from 'framer-motion';
import SectionTitle from '../ui/SectionTitle';

type FormState = 'idle' | 'sending' | 'sent' | 'error';

export default function Contact() {
  const [state, setState] = useState<FormState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: '',
    email: '',
    message: '',
    company: '', // honeypot anti-spam, doit rester vide
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setState('sending');
    setError(null);

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();

      if (!res.ok || !data.ok) {
        throw new Error(data.error || "Erreur lors de l'envoi du message.");
      }

      setState('sent');
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Erreur lors de l'envoi du message.",
      );
      setState('error');
    }
  };

  const inputClass =
    'w-full font-sans text-sm text-forest-dark bg-cream-50 border border-cream-300 rounded-xl px-4 py-3 outline-none focus:border-forest-light focus:ring-2 focus:ring-forest-lightest transition-all placeholder:text-forest/30 resize-none';

  return (
    <section id="contact" className="py-24 bg-cream-100">
      <div className="max-w-2xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.7 }}
        >
          <SectionTitle
            eyebrow="Contact"
            title="Envoyez-nous un message"
            subtitle="Une question ? Un renseignement ? Nous vous répondons dans les plus brefs délais."
          />
        </motion.div>

        <motion.form
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="mt-10 flex flex-col gap-4"
        >
          <input
            type="text"
            name="company"
            value={form.company}
            onChange={handleChange}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            className="absolute left-[-9999px] w-px h-px opacity-0"
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input
              type="text"
              name="name"
              placeholder="Votre nom"
              required
              value={form.name}
              onChange={handleChange}
              className={inputClass}
            />
            <input
              type="email"
              name="email"
              placeholder="Votre email"
              required
              value={form.email}
              onChange={handleChange}
              className={inputClass}
            />
          </div>
          <textarea
            name="message"
            placeholder="Votre message…"
            required
            rows={5}
            value={form.message}
            onChange={handleChange}
            className={inputClass}
          />

          <div className="flex items-center justify-between gap-4 mt-2">
            <button
              type="submit"
              disabled={state === 'sending' || state === 'sent'}
              className="bg-forest text-cream-100 font-sans text-sm px-7 py-3.5 rounded-full hover:bg-forest-dark disabled:opacity-60 transition-colors tracking-wide cursor-pointer disabled:cursor-default"
            >
              {state === 'sending'
                ? 'Envoi…'
                : state === 'sent'
                ? '✓ Message envoyé'
                : 'Envoyer le message'}
            </button>

            {state === 'sent' && (
              <p className="font-sans text-sm text-forest-light">
                Merci ! Nous vous répondrons rapidement.
              </p>
            )}
            {state === 'error' && (
              <p className="font-sans text-sm text-red-600">
                {error || "Une erreur est survenue. Réessayez plus tard."}
              </p>
            )}
          </div>
        </motion.form>
      </div>
    </section>
  );
}
